import { useTranslation } from '@msdk/common'
import {
  FieldDefinition,
  FieldType,
  FormFieldDefinition,
  FormNode,
  FormOpenMode,
  ReloadViewsOptions,
} from '@msdk/definitions'
import { styled, Tooltip } from '@msdk/ui'
import React, { ReactElement } from 'react'

import { ChangeArgs } from '../../models/form'
import { AnyOfField } from './AnyOfField'
import { AttachmentField } from './AttachmentField'
import { CustomField } from './CustomField'
import { DateTimeField } from './DateTimeField'
import { DateTimeFieldUnknown } from './DateTimeFieldUnknown'
import { DurationField } from './DurationField'
import { ListFields } from './ListFields'
import { LookupField } from './LookupField'
import { MetadataSelector } from './MetadataSelector'
import { MultiselectorField } from './MultiselectorField'
import { NumericField } from './NumericField'
import { OptionsetField } from './OptionsetField'
import { StringField } from './StringField'
import { TimeField } from './TimeField'

type CommonProps<T> = {
  type: FieldType
  node: FormNode
  filterField?: boolean
  size?: number
  showLabel?: boolean
  showPlaceholder?: boolean
  useLabelAsPlaceholder?: boolean
  onReload?: (options?: ReloadViewsOptions) => void
} & T

type ConditionalFieldRendererReadonlyProps = {
  readonly: true
}

type ConditionalFieldRendererEditProps = {
  readonly?: false
  onChange: (args: ChangeArgs) => void
}

export type ConditionalFieldRendererBehaviourProps =
  | ConditionalFieldRendererReadonlyProps
  | ConditionalFieldRendererEditProps

type ConditionalFieldRendererProps = CommonProps<ConditionalFieldRendererBehaviourProps>

const emptyOnChange = () => {
  //
}

const Container = styled('div')<{ filterField?: boolean; size?: number; hasCustomUI?: boolean }>`
  padding: 0;
  ${(props) => props.filterField && `margin-bottom: 0px;`}
  ${(props) => props.size && `min-width: 220px;`}
  ${(props) => props.size && !(props.filterField && props.hasCustomUI) && `width: ${props.size}ch;`}

  .MuiFormHelperText-root {
    margin-top: 0;
  }

  .MuiFormControl-root {
    ${(props) => props.filterField && `padding-right: 0;`}
  }
`

const StyledError = styled('div')`
  width: 100%;
  height: 40px;
  background-color: red;
  color: white;
  text-align: center;
  border-radius: 5px;
  padding: 10px;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`

function UnsupportedComponent(props: { node: FormNode; type: FieldType }) {
  const fieldDef = props.node.value.fieldDefinition as FieldDefinition
  const t = useTranslation()

  return (
    <Tooltip title={`FieldName: ${props.node.id}, type: ${props.type}`}>
      <StyledError>{`The field ${t(fieldDef.idml, fieldDef.description)} is not supported`}</StyledError>
    </Tooltip>
  )
}

export function ConditionalFieldRenderer({
  type,
  node,
  filterField,
  onReload,
  ...rest
}: ConditionalFieldRendererProps) {
  const formDef = node.value.formDefinition as FormFieldDefinition
  const showLabel = rest.showLabel ?? !formDef.hideDescription
  const hasCustomUI = Boolean(formDef.customUI)
  const fieldArgs = {
    formOpenMode: rest.readonly ? FormOpenMode.Readonly : FormOpenMode.Edit,
    filterField,
    showLabel,
    showPlaceholder: rest.showPlaceholder,
    useLabelAsPlaceholder: rest.useLabelAsPlaceholder,
    onReload,
  }

  const ruleEngineDebug = window.__APP_CONFIG__.DEBUG_RULE_ENGINE === 'true'

  const onChange = rest.readonly ? emptyOnChange : rest.onChange
  const { size } = rest

  const renderer: Record<FieldType, ReactElement> = {
    Attachment: <AttachmentField onChange={onChange} node={node} fieldArgs={fieldArgs} />,
    Bag: <UnsupportedComponent node={node} type={type} />,
    Custom: <CustomField onChange={onChange} node={node} fieldArgs={fieldArgs} />,
    DateTime: <DateTimeField onChange={onChange} node={node} fieldArgs={fieldArgs} />,
    DateTimeUnknown: <DateTimeFieldUnknown onChange={onChange} node={node} fieldArgs={fieldArgs} />,
    Duration: <DurationField onChange={onChange} node={node} fieldArgs={fieldArgs} />,
    Image: <AttachmentField onChange={onChange} node={node} fieldArgs={fieldArgs} />,
    List: <ListFields onChange={onChange} node={node} fieldArgs={fieldArgs} />,
    Lookup: <LookupField onChange={onChange} node={node} fieldArgs={fieldArgs} />,
    Numeric: <NumericField onChange={onChange} node={node} fieldArgs={fieldArgs} />,
    OptionSet: <OptionsetField onChange={onChange} node={node} fieldArgs={fieldArgs} />,
    String: <StringField onChange={onChange} node={node} fieldArgs={fieldArgs} />,
    MultiSelector: <MultiselectorField onChange={onChange} node={node} fieldArgs={fieldArgs} />,
    MetadataSelector: <MetadataSelector onChange={onChange} node={node} fieldArgs={fieldArgs} />,
    Time: <TimeField onChange={onChange} node={node} fieldArgs={fieldArgs} />,
    AnyOf: <AnyOfField onChange={onChange} node={node} fieldArgs={fieldArgs} />,
    CodeableConcept: <StringField onChange={onChange} node={node} fieldArgs={fieldArgs} />,
  }

  if (node.value.visibility === 'Invisible') {
    return
  }

  return (
    <>
      {ruleEngineDebug && (
        <Tooltip title={node.id}>
          <Container
            filterField={filterField || false}
            hasCustomUI={hasCustomUI}
            className="ConditionalField-renderer"
            size={size}
          >
            {renderer[type]}
          </Container>
        </Tooltip>
      )}
      {!ruleEngineDebug && (
        <Container
          filterField={filterField || false}
          hasCustomUI={hasCustomUI}
          className="ConditionalField-renderer"
          size={size}
        >
          {renderer[type]}
        </Container>
      )}
    </>
  )
}
